import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PlSheet, ProductThumb } from "./ProductListBits";
import {
  ProductActionKey,
  draftEditsLead,
  formatRupee,
  hasUnpublishedEdits,
  productActionGroups,
  productBadge,
  publishEditsLabel,
} from "./productListRow";
import PriceInput from "./PriceInput";
import { NfcTagForm, TagSave } from "./NfcTagForm";
import { PlIcon } from "./plIcons";

const ACTION_ICONS: Record<ProductActionKey, string> = {
  "publish-draft": "clock",
  edit: "edit",
  view: "eye",
  preview: "cert",
  tag: "tag",
  "add-qr": "qr",
  catalogue: "book",
  transfer: "swap",
  "undo-transfer": "undo",
  "undo-billing": "undo",
  variant: "copy",
  archive: "arch",
  unarchive: "arch",
};

type Shared = {
  product: any;
  authUser: any;
  draftRevisionsEnabled: boolean;
  onAction: (key: ProductActionKey, product: any) => void;
};

const snOf = (product: any) =>
  product?.orgSrNo != null && product.orgSrNo !== "" ? `#${product.orgSrNo}` : product?.productUniqueId ? `#${product.productUniqueId}` : "";

/** "2 edits saved as draft. Buyers still see the live version." + Publish (existing confirm flow). */
export const DraftEditsNote = ({ product, onPublish, compact = false }: { product: any; onPublish: () => void; compact?: boolean }) => (
  <div className={`pl-alert is-warn pl-edits-note${compact ? " is-compact" : ""}`}>
    <PlIcon name="clock" size={18} />
    <span className="pl-edits-note__text">
      <b>{draftEditsLead(product)}</b> Buyers still see the live version.
    </span>
    <button type="button" className="pl-btn is-primary is-sm" onClick={onPublish}>
      Publish
    </button>
  </div>
);

/** Phone/tablet ⋯ sheet (P06): photo, name, #SN · ₹price · status, draft banner, Edit/View, price, groups. */
export const ProductActionSheet = ({
  product,
  authUser,
  draftRevisionsEnabled,
  onAction,
  onClose,
  onPriceSave,
  onTagSave,
  onPreview,
}: Shared & {
  onClose: () => void;
  onPriceSave: (product: any, sellPrice: string, costPrice: string) => void;
  onTagSave: TagSave;
  onPreview: (images: any) => void;
}) => {
  const groups = useMemo(
    () => productActionGroups(product, { authUser, draftRevisionsEnabled }),
    [product, authUser, draftRevisionsEnabled]
  );
  const price = formatRupee(product?.sellPrice);
  const status = productBadge(product, draftRevisionsEnabled).label;
  return (
    <PlSheet open={Boolean(product)} onClose={onClose} label={`Actions for ${product?.name || "product"}`} className="pl-action-sheet">
      {(close) => {
        const run = (key: ProductActionKey) => {
          close();
          onAction(key, product);
        };
        return (
          <div className="pl-action-sheet__body">
            <div className="pl-action-sheet__head">
              <ProductThumb
                product={product}
                className="is-mini"
                onPreview={(images) => {
                  // The sheet sits above the photo viewer; close it first so the viewer is usable.
                  close();
                  onPreview(images);
                }}
              />
              <div className="pl-grow">
                <div className="pl-action-sheet__name">{product?.name || "Untitled product"}</div>
                <div className="pl-action-sheet__meta">{[snOf(product), price, status].filter(Boolean).join(" · ")}</div>
              </div>
              <button type="button" className="pl-iconbtn is-plain" aria-label="Close" onClick={close}>
                <PlIcon name="x" />
              </button>
            </div>
            {hasUnpublishedEdits(product, draftRevisionsEnabled) ? (
              <DraftEditsNote product={product} onPublish={() => run("publish-draft")} />
            ) : null}
            <div className="pl-action-sheet__main">
              <button type="button" className="pl-btn is-wide" onClick={() => run("edit")}>
                <PlIcon name="edit" />
                Edit
              </button>
              <button type="button" className="pl-btn is-wide" onClick={() => run("view")}>
                <PlIcon name="eye" />
                View
              </button>
            </div>
            <div className="pl-action-group" data-section="prices">
              <div className="pl-sec">Price · only your team sees</div>
              <div className="pl-action-sheet__prices">
                <label className="pl-field">
                  <span>Selling ₹</span>
                  <PriceInput
                    value={product?.sellPrice}
                    label="Selling price"
                    className="is-field"
                    onCommit={(next) => onPriceSave(product, next, String(product?.costPrice ?? ""))}
                  />
                </label>
                <label className="pl-field">
                  <span>Cost ₹</span>
                  <PriceInput
                    value={product?.costPrice}
                    label="Cost price"
                    emptyText="Not entered"
                    className="is-field"
                    onCommit={(next) => onPriceSave(product, String(product?.sellPrice ?? ""), next)}
                  />
                </label>
              </div>
            </div>
            {groups.map((group) => (
              <div key={group.key} className="pl-action-group">
                <div className="pl-sec">{group.title}</div>
                {group.actions.map((action) =>
                  action.key === "tag" ? (
                    <NfcTagForm key={action.key} product={product} onSave={onTagSave} />
                  ) : (
                  <button key={action.key} type="button" className="pl-action" onClick={() => run(action.key)}>
                    <PlIcon name={ACTION_ICONS[action.key]} />
                    <span className="pl-grow">{action.label}</span>
                    {action.hint ? <span className="pl-meta">{action.hint}</span> : null}
                  </button>
                  )
                )}
              </div>
            ))}
          </div>
        );
      }}
    </PlSheet>
  );
};

const MENU_GAP = 5;
const MENU_EDGE = 8;

/**
 * Where the row menu goes: below the button when it fits (or when there is more room below),
 * otherwise above. max-height is the room actually left on that side, so the menu's own scroll
 * reaches every action instead of running off the screen.
 */
export function rowMenuPosition(
  anchor: { top: number; bottom: number; right: number },
  viewportWidth: number,
  viewportHeight: number,
  wanted = 440
): React.CSSProperties {
  const below = viewportHeight - anchor.bottom - MENU_GAP - MENU_EDGE;
  const above = anchor.top - MENU_GAP - MENU_EDGE;
  const up = below < wanted && above > below;
  const room = Math.max(120, up ? above : below);
  return {
    position: "fixed",
    right: Math.max(MENU_EDGE, viewportWidth - anchor.right),
    ...(up ? { bottom: viewportHeight - anchor.top + MENU_GAP } : { top: anchor.bottom + MENU_GAP }),
    maxHeight: room,
  };
}

/**
 * Laptop ⋯ row menu (D02): publish draft edits, Edit (E), View, Preview certificate, Sell & share,
 * Archive. HKP tag and QR are the icon buttons beside it. Portal, fixed to the button, flips up near
 * the bottom of the window.
 */
export const ProductRowMenu = ({ product, authUser, draftRevisionsEnabled, onAction }: Shared) => {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const groups = useMemo(
    () => (anchor ? productActionGroups(product, { authUser, draftRevisionsEnabled }) : []),
    [anchor, product, authUser, draftRevisionsEnabled]
  );
  const close = () => setAnchor(null);
  useEffect(() => {
    if (!anchor) return undefined;
    // The menu is pinned to where the button was; scrolling would leave it behind.
    const onMove = (event: Event) => {
      if ((event.target as Element)?.closest?.(".pl-menu")) return;
      setAnchor(null);
    };
    window.addEventListener("scroll", onMove, true);
    return () => window.removeEventListener("scroll", onMove, true);
  }, [anchor]);
  const run = (key: ProductActionKey) => {
    close();
    onAction(key, product);
  };
  const find = (key: ProductActionKey) => groups.flatMap((g) => g.actions).find((a) => a.key === key);
  const preview = find("preview");
  const sell = groups.find((g) => g.key === "sell")?.actions || [];
  const archive = groups.find((g) => g.key === "more")?.actions.filter((a) => a.key === "archive" || a.key === "unarchive") || [];
  const edits = hasUnpublishedEdits(product, draftRevisionsEnabled);
  const style = anchor ? rowMenuPosition(anchor, window.innerWidth, window.innerHeight) : undefined;
  const item = (key: ProductActionKey, label: string, extra?: React.ReactNode, className = "") => (
    <button key={key} type="button" role="menuitem" className={`pl-menu__item ${className}`} onClick={() => run(key)}>
      <PlIcon name={ACTION_ICONS[key]} size={17} />
      <span className="pl-grow">{label}</span>
      {extra}
    </button>
  );
  return (
    <>
      <button
        type="button"
        className={`pl-iconbtn${anchor ? " is-active" : ""}`}
        aria-label={`More actions for ${product?.name || "product"}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(anchor ? null : event.currentTarget.getBoundingClientRect());
        }}
      >
        <PlIcon name="more" size={18} />
      </button>
      {anchor
        ? createPortal(
            <div
              className="pl-menu-layer"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") close();
                if ((event.key === "e" || event.key === "E") && !event.metaKey && !event.ctrlKey) {
                  event.preventDefault();
                  run("edit");
                }
              }}
            >
              <div className="pl-menu" role="menu" style={style}>
                {edits ? (
                  <>
                    <button type="button" role="menuitem" className="pl-menu__item is-orange" onClick={() => run("publish-draft")} autoFocus>
                      <PlIcon name="clock" size={17} />
                      <span className="pl-grow">{publishEditsLabel(product)}</span>
                    </button>
                    <div className="pl-menu__line" />
                  </>
                ) : null}
                <button type="button" role="menuitem" className="pl-menu__item" onClick={() => run("edit")} autoFocus={!edits}>
                  <PlIcon name="edit" size={17} />
                  <span className="pl-grow">Edit</span>
                  <span className="pl-kbd">E</span>
                </button>
                {item("view", "View")}
                {preview ? item("preview", preview.label) : null}
                {sell.length ? (
                  <>
                    <div className="pl-menu__line" />
                    <div className="pl-menu__h">Sell & share</div>
                    {sell.map((a) => item(a.key, a.label, a.hint ? <span className="pl-meta">{a.hint}</span> : null))}
                  </>
                ) : null}
                {archive.length ? (
                  <>
                    <div className="pl-menu__line" />
                    {archive.map((a) => item(a.key, a.label, a.hint ? <span className="pl-meta">{a.hint}</span> : null))}
                  </>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

/** Laptop: the row's tag button opens the same form in a small popover instead of a modal. */
export const NfcTagButton = ({ product, label, onSave }: { product: any; label: string; onSave: TagSave }) => {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const bound = Boolean(String(product?.xuid ?? "").trim());
  const close = () => setAnchor(null);
  return (
    <>
      <button
        type="button"
        className={`pl-iconbtn${bound ? " is-bound" : ""}${anchor ? " is-active" : ""}`}
        aria-label={bound ? `NFC bound: ${String(product?.xuid ?? "").trim()}` : label}
        title={bound ? `NFC bound · ${String(product?.xuid ?? "").trim()}` : label}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(anchor ? null : event.currentTarget.getBoundingClientRect());
        }}
      >
        <PlIcon name="tag" size={18} />
      </button>
      {anchor
        ? createPortal(
            <div
              className="pl-menu-layer"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") close();
              }}
            >
              <div
                className="pl-menu pl-nfc-pop"
                role="dialog"
                aria-label={`HKP tag for ${product?.name || "product"}`}
                style={rowMenuPosition(anchor, window.innerWidth, window.innerHeight, 260)}
              >
                <div className="pl-menu__h">HKP tag · NFC</div>
                <NfcTagForm
                  product={product}
                  autoFocus={!bound}
                  onSave={async (p, srNo, xuid) => {
                    const ok = await onSave(p, srNo, xuid);
                    if (ok) close();
                    return ok;
                  }}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};
